import { isAxiosError } from 'axios';

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, milliseconds));

/**
 * Run `attempt`, and on a failure run it again after each delay in turn, so a
 * dropped request or a moment offline costs a short wait instead of the whole
 * load. Gives up early once `shouldStop` says the result is no longer wanted,
 * or at once on a failure `isRetryable` says another try cannot fix, and
 * rejects with the last failure when every try has failed.
 *
 * With `startNextTryAfterMs`, a try that has not answered by then is not given
 * up: the next try starts beside it (without the delay), and whichever answers
 * first with a success is the answer. A slow success is never thrown away for
 * a retry, and a connection that hangs costs that wait, not the try's whole
 * timeout. The number of tries is the same.
 */
export async function retryWithBackoff<T>(
  attempt: () => Promise<T>,
  delaysMs: readonly number[],
  shouldStop: () => boolean = () => false,
  isRetryable: (error: unknown) => boolean = () => true,
  startNextTryAfterMs?: number,
): Promise<T> {
  if (typeof attempt !== 'function') {
    throw new TypeError('retryWithBackoff: attempt must be a function');
  }
  if (startNextTryAfterMs !== undefined) {
    return retryKeepingSlowTries(attempt, delaysMs, shouldStop, isRetryable, startNextTryAfterMs);
  }
  const tryFrom = async (delayIndex: number): Promise<T> => {
    try {
      return await attempt();
    } catch (error) {
      if (delayIndex >= delaysMs.length || shouldStop() || !isRetryable(error)) throw error;
      await wait(delaysMs[delayIndex]);
      if (shouldStop()) throw error;
      return tryFrom(delayIndex + 1);
    }
  };
  return tryFrom(0);
}

function retryKeepingSlowTries<T>(
  attempt: () => Promise<T>,
  delaysMs: readonly number[],
  shouldStop: () => boolean,
  isRetryable: (error: unknown) => boolean,
  startNextTryAfterMs: number,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const tryCount = delaysMs.length + 1;
    const timers = new Set<ReturnType<typeof setTimeout>>();
    let triesStarted = 0;
    let triesRunning = 0;
    let nextTryScheduled = false;
    let settled = false;
    let lastFailure: unknown;

    const after = (milliseconds: number, run: () => void) => {
      const timer = setTimeout(() => {
        timers.delete(timer);
        run();
      }, milliseconds);
      timers.add(timer);
    };
    const settle = (outcome: () => void) => {
      if (settled) return;
      settled = true;
      timers.forEach(clearTimeout);
      timers.clear();
      outcome();
    };
    const canStartAnother = () => !settled && triesStarted < tryCount && !shouldStop();
    const failIfNothingLeft = () => {
      if (triesRunning === 0 && !nextTryScheduled) settle(() => reject(lastFailure));
    };
    const scheduleNextTry = (delay: number) => {
      if (nextTryScheduled || triesStarted >= tryCount) return;
      nextTryScheduled = true;
      after(delay, () => {
        nextTryScheduled = false;
        if (canStartAnother()) startTry();
        else failIfNothingLeft();
      });
    };
    const startTry = () => {
      const tryIndex = triesStarted;
      let answered = false;
      triesStarted += 1;
      triesRunning += 1;
      after(startNextTryAfterMs, () => {
        const noLaterTry = triesStarted === tryIndex + 1 && !nextTryScheduled;
        if (!answered && noLaterTry && canStartAnother()) startTry();
      });
      attempt().then(
        (value) => {
          answered = true;
          triesRunning -= 1;
          settle(() => resolve(value));
        },
        (failure: unknown) => {
          answered = true;
          triesRunning -= 1;
          lastFailure = failure;
          if (settled) return;
          if (!isRetryable(failure) || shouldStop()) {
            settle(() => reject(failure));
            return;
          }
          if (triesStarted === tryIndex + 1) scheduleNextTry(delaysMs[tryIndex]);
          failIfNothingLeft();
        },
      );
    };
    startTry();
  });
}

// 4xx answers that say "try again later" rather than "no".
const RETRYABLE_CLIENT_STATUSES: readonly number[] = [408, 429];

/**
 * Whether a failed request is worth another try: not when the server answered
 * with a 4xx (an entitlement denial, a bad request), which the same request
 * gets again - except a request timeout (408) or too many requests (429). A
 * network error, a timeout or a 5xx may pass.
 */
export function isRetryableRequestFailure(error: unknown): boolean {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  if (status === undefined || status < 400 || status >= 500) return true;
  return RETRYABLE_CLIENT_STATUSES.includes(status);
}
